import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link, useLocation, useParams } from "react-router-dom";
import api from "../../api";
import { formatScorePercent } from "../../utils/formatScorePercent";
import TutorQuestionReviewCard from "./components/TutorQuestionReviewCard";
import { reviewEndpoint } from "./tutorReviewUtils";

export default function TutorStudentResourceReview() {
  const { buyerId, resourceKind, reviewId } = useParams();
  const location = useLocation();
  const rowMeta = location.state?.row || null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(reviewEndpoint(buyerId, resourceKind, reviewId));
        if (!cancelled) {
          if (res.data?.success) {
            setData(res.data.data);
          } else {
            setError(res.data?.message || "Failed to load review");
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || "Failed to load review");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buyerId, resourceKind, reviewId]);

  const resource = data?.resource || {};
  const tests = data?.tests || [];
  const title = rowMeta?.name || resource.name || "Resource";
  const subtitle = [
    rowMeta?.type_label || resource.type_label,
    resource.name && resource.name !== title ? resource.name : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box>
      <Button
        component={Link}
        to={`/tutor/students/${buyerId}`}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        Back to student resources
      </Button>

      <Paper sx={{ p: { xs: 2, sm: 3 } }} elevation={1}>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
          Review attempt
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle || title}
        </Typography>
        {rowMeta?.score != null && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Score: <strong>{formatScorePercent(rowMeta)}</strong>
            {rowMeta.completed_at ? ` · Completed ${rowMeta.completed_at}` : ""}
          </Typography>
        )}

        <Divider sx={{ mb: 2 }} />

        {loading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && <Typography color="error">{error}</Typography>}

        {!loading && !error && tests.length === 0 && (
          <Typography color="text.secondary">
            This resource is completed, but there are no recorded question attempts to review.
          </Typography>
        )}

        {!loading &&
          !error &&
          tests.map((test) => (
            <Box key={test.test_id} sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap">
                <Typography fontWeight={700}>{test.test_name}</Typography>
                {test.attempt_number != null && (
                  <Chip size="small" label={`Attempt #${test.attempt_number}`} />
                )}
                <Chip
                  size="small"
                  color="info"
                  label={`${test.correct_count ?? 0}/${test.total_questions ?? 0} correct`}
                />
                {test.answered_count != null && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${test.answered_count}/${test.total_questions ?? 0} answered`}
                  />
                )}
              </Stack>
              {(test.questions || []).map((question, idx) => (
                <TutorQuestionReviewCard
                  key={question.question_id || idx}
                  question={question}
                  index={idx}
                />
              ))}
            </Box>
          ))}
      </Paper>
    </Box>
  );
}
